import { useForm } from '@mantine/form';
import {
  DEFAULT_SMART_ASSISTANT_COLOR_THEME,
  SMART_ASSISTANT_GRADIENT_COLORS_COUNT
} from 'shared/constants/smart-assistant';
import {
  ConfigurationFormValues,
  CustomizationFormValues,
  SmartAssistant,
  SmartAssistantModel
} from 'shared/interfaces/SmartAssistant';
import {
  CUSTOM_PROMPT_MAX_LENGTH,
  SMART_ASSISTANT_NAME_MAX_LENGTH,
  SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH,
  maxLengthValidator,
  requiredInputValidator,
  validateHexColor,
  validateHexColors,
  validateStrictArrayCount
} from 'shared/utils/form-validators';

export function useShopifySmartAssistantForms(smartAssistant?: SmartAssistant) {
  const configurationForm = useForm<ConfigurationFormValues>({
    initialValues: {
      customSystemPrompt: smartAssistant?.customSystemPrompt ?? '',
      enabledEmoji: smartAssistant?.enabledEmoji ?? false,
      model: smartAssistant?.model ?? SmartAssistantModel.GPT_4_O_MINI,
      restrictAnswersToResourcesContent: smartAssistant?.restrictAnswersToResourcesContentResponse ? true : false,
      restrictAnswersToResourcesContentResponse: smartAssistant?.restrictAnswersToResourcesContentResponse ?? ''
    } as ConfigurationFormValues,
    validate: {
      customSystemPrompt: (value) =>
        requiredInputValidator(value) || maxLengthValidator(value, CUSTOM_PROMPT_MAX_LENGTH),
      model: (value) => requiredInputValidator(value),
      restrictAnswersToResourcesContentResponse: (value, values) =>
        values.restrictAnswersToResourcesContent ? requiredInputValidator(value) : null
    }
  });

  const customizationForm = useForm({
    initialValues: {
      name: smartAssistant?.name ?? '',
      welcomeMessage: smartAssistant?.welcomeMessage ?? '',
      assistantMessageColor:
        smartAssistant?.assistantMessageColor ?? DEFAULT_SMART_ASSISTANT_COLOR_THEME.assistantMessageColor,
      userMessageColor: smartAssistant?.userMessageColor ?? DEFAULT_SMART_ASSISTANT_COLOR_THEME.userMessageColor,
      conversationBackgroundColor:
        smartAssistant?.conversationBackgroundColor ?? DEFAULT_SMART_ASSISTANT_COLOR_THEME.conversationBackgroundColor,
      secondaryBackgroundColor:
        smartAssistant?.secondaryBackgroundColor ?? DEFAULT_SMART_ASSISTANT_COLOR_THEME.secondaryBackgroundColor,
      gradientColors: smartAssistant?.gradientColors
        ? smartAssistant?.gradientColors
        : DEFAULT_SMART_ASSISTANT_COLOR_THEME.gradientColors,
      showBadge: smartAssistant?.showBadge ?? true
    } as CustomizationFormValues,
    validate: {
      name: (value) => requiredInputValidator(value) || maxLengthValidator(value, SMART_ASSISTANT_NAME_MAX_LENGTH),
      welcomeMessage: (value) =>
        value !== undefined ? maxLengthValidator(value, SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH) : null,
      assistantMessageColor: (value) =>
        value !== undefined ? requiredInputValidator(value) || validateHexColor(value) : null,
      userMessageColor: (value) =>
        value !== undefined ? requiredInputValidator(value) || validateHexColor(value) : null,
      conversationBackgroundColor: (value) =>
        value !== undefined ? requiredInputValidator(value) || validateHexColor(value) : null,
      secondaryBackgroundColor: (value) =>
        value !== undefined ? requiredInputValidator(value) || validateHexColor(value) : null,
      gradientColors: (value) =>
        value
          ? validateHexColors(value) || validateStrictArrayCount(value, [0, SMART_ASSISTANT_GRADIENT_COLORS_COUNT])
          : null
    }
  });

  return {
    configurationForm,
    customizationForm
  };
}
